import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Trash2, Pencil, FileX, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { DateField } from "@/components/DateField";
import api, { fmt, fmtDate, errText, undoToast } from "@/lib/api";

const MODES = ["Cash", "UPI", "Bank"];
const EXPENSE_CATS = ["Tea", "Petrol", "Transport", "Packing", "Food", "Electricity", "Internet", "Office Expense", "Miscellaneous"];
const today = () => format(new Date(), "yyyy-MM-dd");

export const CONFIGS = {
  sales: {
    title: "Daily Sales",
    fields: [
      { k: "date", t: "date", label: "Date" },
      { k: "amount", t: "number", label: "Sale Amount ₹" },
      { k: "payment_mode", t: "select", label: "Payment Mode", options: MODES },
      { k: "notes", t: "text", label: "Notes" },
    ],
  },
  expenses: {
    title: "Daily Expenses",
    fields: [
      { k: "date", t: "date", label: "Date" },
      { k: "category", t: "select", label: "Expense Category", options: EXPENSE_CATS },
      { k: "amount", t: "number", label: "Amount ₹" },
      { k: "payment_mode", t: "select", label: "Payment Mode", options: MODES },
      { k: "notes", t: "text", label: "Notes" },
    ],
  },
  "supplier-payments": {
    title: "Supplier Payments",
    fields: [
      { k: "supplier_name", t: "text", label: "Supplier Name" },
      { k: "date", t: "date", label: "Date" },
      { k: "amount", t: "number", label: "Amount ₹" },
      { k: "payment_mode", t: "select", label: "Payment Mode", options: MODES },
      { k: "status", t: "select", label: "Payment Status", options: ["Paid", "Pending"] },
      { k: "notes", t: "text", label: "Notes" },
    ],
  },
  salaries: {
    title: "Salary Management",
    fields: [
      { k: "employee_name", t: "text", label: "Employee Name" },
      { k: "month", t: "text", label: "Month", placeholder: "e.g. June 2026" },
      { k: "amount", t: "number", label: "Salary Amount ₹" },
      { k: "paid_date", t: "date", label: "Paid Date" },
      { k: "payment_mode", t: "select", label: "Payment Mode", options: MODES },
      { k: "status", t: "select", label: "Status", options: ["Paid", "Pending"] },
    ],
  },
  "other-income": {
    title: "Other Income",
    fields: [
      { k: "date", t: "date", label: "Date" },
      { k: "source", t: "text", label: "Income Source" },
      { k: "amount", t: "number", label: "Amount ₹" },
      { k: "notes", t: "text", label: "Notes" },
    ],
  },
};

const emptyEntry = (cfg) => {
  const e = {};
  cfg.fields.forEach((f) => {
    e[f.k] = f.t === "date" ? today() : f.t === "number" ? "" : f.t === "select" ? f.options[0] : "";
  });
  return e;
};

const RANGES = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "week", label: "Last 7 Days" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

const inRange = (d, range, from, to) => {
  if (!d) return range === "all";
  const t = today();
  switch (range) {
    case "today": return d === t;
    case "week": {
      const w = new Date(); w.setDate(w.getDate() - 7);
      return d >= format(w, "yyyy-MM-dd") && d <= t;
    }
    case "month": return d.startsWith(t.slice(0, 7));
    case "custom": return (!from || d >= from) && (!to || d <= to);
    default: return true;
  }
};

export default function BAEntries({ type }) {
  const cfg = CONFIGS[type];
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("All");
  const [status, setStatus] = useState("All");
  const [cat, setCat] = useState("All");

  const dateKey = type === "salaries" ? "paid_date" : "date";
  const hasMode = cfg.fields.some((f) => f.k === "payment_mode");
  const hasStatus = cfg.fields.some((f) => f.k === "status");
  const hasCat = cfg.fields.some((f) => f.k === "category");
  const hasFilters = q || range !== "all" || mode !== "All" || status !== "All" || cat !== "All";

  const clearFilters = () => { setQ(""); setRange("all"); setFrom(""); setTo(""); setMode("All"); setStatus("All"); setCat("All"); };

  const { data: entries, isLoading } = useQuery({
    queryKey: ["ba", type],
    queryFn: async () => (await api.get(`/ba/${type}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["ba", type] });
    qc.invalidateQueries({ queryKey: ["ba-dashboard"] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...dialog.data, amount: Number(dialog.data.amount) || 0 };
      return dialog.id ? (await api.put(`/ba/${type}/${dialog.id}`, body)).data : (await api.post(`/ba/${type}`, body)).data;
    },
    onSuccess: () => { toast.success(dialog.id ? "Entry updated" : "Entry added"); refresh(); setDialog(null); },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: async (eid) => (await api.delete(`/ba/${type}/${eid}`)).data,
    onSuccess: (data) => { undoToast("Entry deleted", data.undo_id, refresh); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });

  const filtered = (entries || []).filter((e) => {
    if (!inRange(e[dateKey] || "", range, from, to)) return false;
    if (mode !== "All" && e.payment_mode !== mode) return false;
    if (status !== "All" && e.status !== status) return false;
    if (cat !== "All" && e.category !== cat) return false;
    if (q) {
      const hay = cfg.fields.map((f) => String(e[f.k] ?? "")).join(" ").toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const total = filtered.reduce((s, e) => s + (e.amount || 0), 0);
  const setField = (k, v) => setDialog({ ...dialog, data: { ...dialog.data, [k]: v } });

  return (
    <div className="space-y-4" data-testid={`ba-${type}-page`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">{cfg.title}</h2>
          <p className="text-sm text-muted-foreground">{filtered.length}{hasFilters ? ` of ${entries?.length || 0}` : ""} entries · Total <span className="font-medium text-foreground">{fmt(total)}</span></p>
        </div>
        <Button data-testid={`ba-add-${type}-button`} className="rounded-full active:scale-95 transition-transform" onClick={() => setDialog({ id: null, data: emptyEntry(cfg) })}>
          <Plus className="h-4 w-4 mr-1.5" /> Add Entry
        </Button>
      </div>

      <div className="rounded-xl border bg-card p-4 space-y-3" data-testid={`ba-filters-${type}`}>
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`ba-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${range === r.key ? "bg-amber-500/15 text-amber-500 border-amber-500/40" : "bg-card text-muted-foreground hover:text-foreground"}`}
            >
              {r.label}
            </button>
          ))}
          {range === "custom" && (
            <div className="flex items-center gap-2">
              <DateField testId="ba-filter-from" value={from} onChange={setFrom} />
              <span className="text-xs text-muted-foreground">to</span>
              <DateField testId="ba-filter-to" value={to} onChange={setTo} />
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input data-testid="ba-filter-search" className="pl-8 h-9" placeholder="Search entries..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {hasCat && (
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="ba-filter-category" className="w-[160px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["All", ...EXPENSE_CATS].map((c) => <SelectItem key={c} value={c}>{c === "All" ? "All Categories" : c}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {hasMode && (
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="ba-filter-mode" className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["All", ...MODES].map((m) => <SelectItem key={m} value={m}>{m === "All" ? "All Modes" : m}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {hasStatus && (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="ba-filter-status" className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{["All", "Paid", "Pending"].map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All Status" : s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {hasFilters && (
            <Button variant="ghost" size="sm" data-testid="ba-clear-filters" className="h-9 text-muted-foreground" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-12 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-10 text-center">
          <FileX className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">{hasFilters ? "No entries match your filters." : "No entries yet. Add your first one."}</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {cfg.fields.map((f) => (
                  <TableHead key={f.k} className={f.t === "number" ? "text-right" : ""}>{f.label.replace(" ₹", "")}</TableHead>
                ))}
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`ba-row-${e.id}`} className="hover:bg-muted/50 transition-colors">
                  {cfg.fields.map((f) => (
                    <TableCell key={f.k} className={f.t === "number" ? "text-right font-medium" : ""}>
                      {f.t === "number" ? fmt(e[f.k]) :
                        f.t === "date" ? fmtDate(e[f.k]) :
                        f.k === "status" ? <StatusBadge status={e[f.k]} /> :
                        (e[f.k] || "—")}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`ba-edit-${e.id}`} onClick={() => setDialog({ id: e.id, data: { ...emptyEntry(cfg), ...Object.fromEntries(cfg.fields.map((f) => [f.k, e[f.k] ?? ""])) } })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" data-testid={`ba-delete-${e.id}`} onClick={() => window.confirm("Delete this entry?") && remove.mutate(e.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{dialog?.id ? "Edit Entry" : `New ${cfg.title} Entry`}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                {cfg.fields.map((f) => (
                  <div key={f.k} className={`space-y-2 ${f.k === "notes" ? "col-span-2" : ""}`}>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">{f.label}</Label>
                    {f.t === "date" ? (
                      <DateField testId={`ba-field-${f.k}`} value={dialog.data[f.k]} onChange={(v) => setField(f.k, v)} />
                    ) : f.t === "select" ? (
                      <Select value={dialog.data[f.k]} onValueChange={(v) => setField(f.k, v)}>
                        <SelectTrigger data-testid={`ba-field-${f.k}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid={`ba-field-${f.k}`} type={f.t === "number" ? "number" : "text"} min={f.t === "number" ? "0" : undefined} placeholder={f.placeholder || ""} value={dialog.data[f.k]} onChange={(ev) => setField(f.k, ev.target.value)} />
                    )}
                  </div>
                ))}
              </div>
              <Button data-testid="ba-save-button" className="w-full rounded-lg" disabled={save.isPending} onClick={() => save.mutate()}>
                {dialog.id ? "Update Entry" : "Save Entry"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
